/**
 * Puts a cut-out person together on the GPU for the preview: the editor plays
 * the matte video of a clip (picture and matte side by side in one frame,
 * packages/shared/src/video/cutout.ts), a small shader multiplies the picture
 * by its matte into a canvas with transparency. The player draws that canvas
 * like any other picture – right after rendering it, so one WebGL canvas
 * serves every person of a frame.
 */

export interface FractionRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

const VERTEX = `
attribute vec2 a_position;
varying vec2 v_uv;
void main() {
  v_uv = vec2((a_position.x + 1.0) * 0.5, (1.0 - a_position.y) * 0.5);
  gl_Position = vec4(a_position, 0.0, 1.0);
}`;

const FRAGMENT = `
precision mediump float;
uniform sampler2D u_frame;
uniform vec4 u_color;
uniform vec4 u_alpha;
varying vec2 v_uv;
void main() {
  vec3 rgb = texture2D(u_frame, u_color.xy + v_uv * u_color.zw).rgb;
  float alpha = texture2D(u_frame, u_alpha.xy + v_uv * u_alpha.zw).r;
  // Premultiplied, as the canvas expects.
  gl_FragColor = vec4(rgb * alpha, alpha);
}`;

interface Gl {
  gl: WebGLRenderingContext;
  texture: WebGLTexture;
  color: WebGLUniformLocation;
  alpha: WebGLUniformLocation;
}

export class MatteCompositor {
  private readonly canvas = document.createElement("canvas");
  /** undefined: not tried yet, null: no WebGL (the person is left out). */
  private state: Gl | null | undefined;

  private setup(): Gl | null {
    const gl = this.canvas.getContext("webgl", {
      alpha: true,
      premultipliedAlpha: true,
      antialias: false,
      depth: false,
      stencil: false,
    });
    if (!gl) return null;
    const shader = (type: number, source: string) => {
      const created = gl.createShader(type)!;
      gl.shaderSource(created, source);
      gl.compileShader(created);
      return created;
    };
    const program = gl.createProgram()!;
    gl.attachShader(program, shader(gl.VERTEX_SHADER, VERTEX));
    gl.attachShader(program, shader(gl.FRAGMENT_SHADER, FRAGMENT));
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return null;
    // biome-ignore lint/correctness/useHookAtTopLevel: a WebGL call, not a React hook
    gl.useProgram(program);
    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    const position = gl.getAttribLocation(program, "a_position");
    gl.enableVertexAttribArray(position);
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);
    const texture = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D, texture);
    // Video frames are not powers of two: no mipmaps, clamped edges.
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.uniform1i(gl.getUniformLocation(program, "u_frame"), 0);
    return {
      gl,
      texture,
      color: gl.getUniformLocation(program, "u_color")!,
      alpha: gl.getUniformLocation(program, "u_alpha")!,
    };
  }

  /**
   * The person of the current frame of `video` as a canvas with
   * transparency (the size of the picture part), or null without WebGL.
   * Draw it right away: the next call reuses the canvas.
   */
  render(
    video: HTMLVideoElement,
    layout: { color: FractionRect; alpha: FractionRect },
  ): HTMLCanvasElement | null {
    if (this.state === undefined) this.state = this.setup();
    const state = this.state;
    if (!state || video.videoWidth === 0) return null;
    const { gl } = state;
    const width = Math.max(2, Math.round(layout.color.width * video.videoWidth));
    const height = Math.max(2, Math.round(layout.color.height * video.videoHeight));
    if (this.canvas.width !== width) this.canvas.width = width;
    if (this.canvas.height !== height) this.canvas.height = height;
    gl.viewport(0, 0, width, height);
    gl.bindTexture(gl.TEXTURE_2D, state.texture);
    try {
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB, gl.RGB, gl.UNSIGNED_BYTE, video);
    } catch {
      return null;
    }
    const { color, alpha } = layout;
    gl.uniform4f(state.color, color.x, color.y, color.width, color.height);
    gl.uniform4f(state.alpha, alpha.x, alpha.y, alpha.width, alpha.height);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    return this.canvas;
  }
}
